import { z } from 'zod'
import { PageQuery } from './pagination.ts'
import { ISODate, Uuid, Version, blankAsAbsent, clearable } from './primitives.ts'

/**
 * ══ HỢP ĐỒNG API — CHỨNG TỪ AMIS · ĐẨY MISA (F24) ═════════════════════════════════════════════
 * Nguồn sự thật duy nhất cho `/api/v1/chung-tu/*`.
 */

export const DocSource = z.enum(['ar', 'ap', 'cash', 'offset'])

export const GenerateDocBody = z.object({
  source: DocSource,
  sourceId: Uuid,
})

/** ⚠ TRẦN 200 nguồn một lô — cả lô nằm trong MỘT giao dịch, không khoá bảng hàng phút. */
export const GenerateBatchBody = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
})

export const DocStepBody = z.object({ version: Version })

/**
 * ✓ XÁC NHẬN ĐÃ NẠP MISA (soát 16/10, T53 — bản cũ `confirmPush` chế độ mô phỏng, `AmisDocs.tsx:385-410`): mỗi chứng từ
 * kèm `version` đang thấy. ⚠ TRẦN 200 một lô — cùng trần với mọi cửa lô của sổ chứng từ.
 */
export const ConfirmLoadedBody = z.object({
  items: z.array(z.object({ id: Uuid, version: Version })).min(1).max(200),
})
export type ConfirmLoadedBodyT = z.infer<typeof ConfirmLoadedBody>

/** Ba nhóm mẫu nạp MISA — bản cũ lọc bằng đúng ba mục này (`AmisDocs.tsx` `fType`). */
export const DocTypeFilter = z.enum(['purchase_service', 'purchase_goods', 'other_journal'])

export const ListDocQuery = PageQuery.extend({
  source: blankAsAbsent(DocSource),
  status: blankAsAbsent(z.enum(['draft', 'queued', 'pushed', 'rejected'])),
  docType: blankAsAbsent(DocTypeFilter),
  from: blankAsAbsent(ISODate),
  to: blankAsAbsent(ISODate),
  /** Một ô tìm: số chứng từ · tên đối tượng · MST · mã phiếu nguồn. */
  q: blankAsAbsent(z.string().max(120)),
  /** KHỚP CHÍNH XÁC số chứng từ HOẶC mã phiếu nguồn — lối mở `/chung-tu?open=<số>` (rà 16/10 L56: `q` ILIKE trả 50 dòng, số
   *  ngắn như "TDV1" có thể rơi ngoài 50 dòng đầu). */
  no: blankAsAbsent(z.string().max(120)),
})

/* ══ SỬA ĐỊNH KHOẢN · XOÁ · GỠ CỜ ĐÃ ĐẨY · LẬP TAY NGHIỆP VỤ KHÁC (`0123`) ═══════════════════ */

/**
 * ✏️ SỬA DÒNG của chứng từ CHƯA đẩy. ⚠ KHÔNG CÓ `amount`: thành tiền đã qua phép kiểm "Σ dòng = tổng
 * chứng từ" lúc lập; sửa lẻ một dòng là phá bất biến đó. Sai tiền thì sửa phiếu nguồn rồi dựng lại.
 */
export const EditDocLine = z.object({
  lineNo: z.number().int().min(1),
  description: z.string().max(500).optional(),
  expenseItem: z.string().max(60).nullable().optional(),
  debitAcc: z.string().max(20).optional(),
  creditAcc: z.string().max(20).optional(),
  costObject: z.string().max(60).nullable().optional(),
  quantity: z.number().nonnegative().nullable().optional(),
  invoiceNo: z.string().max(60).nullable().optional(),
  /** ⭐ `clearable`: `''` và `null` cùng nghĩa XOÁ ngày hóa đơn — vắng khoá mới là giữ nguyên. */
  invoiceDate: clearable(ISODate),
  vatRate: z.number().min(0).max(100).nullable().optional(),
})

export const EditDocBody = z.object({
  version: Version,
  partnerName: z.string().max(200).optional(),
  partnerTax: z.string().max(20).nullable().optional(),
  lines: z.array(EditDocLine).max(200).optional(),
})

/**
 * ✍️ LẬP TAY "NGHIỆP VỤ KHÁC" (bản cũ `CreateNvkModal`). Mỗi dòng là MỘT bút toán Nợ/Có đủ cặp —
 * `@rs/accounting.createDoc` vẫn cưỡng chế Σ dòng = tổng chứng từ, nên không cần gửi tổng riêng.
 */
export const CreateJournalBody = z.object({
  docDate: ISODate,
  /**
   * ⭐ NGÀY HẠCH TOÁN — kỳ mà bút toán RƠI VÀO SỔ, khác ngày chứng từ. Hoá đơn đề 28/02 nhưng kế
   * toán ghi sổ vào kỳ 03 là chuyện thường; gộp hai ngày làm một thì chứng từ rơi sai kỳ và gỡ ra
   * phải huỷ rồi lập lại. Bỏ trống ⇒ máy chủ lấy `docDate`, đúng hành vi trước đây.
   */
  accountingDate: blankAsAbsent(ISODate),
  partnerName: z.string().max(200).default(''),
  partnerTax: blankAsAbsent(z.string().max(20)),
  note: z.string().max(500).default(''),
  /** DX (phiếu quản trị) — bản cũ mặc định BẬT cho chứng từ lập tay. */
  mgmtOnly: z.boolean().default(true),
  lines: z.array(z.object({
    debitAcc: z.string().min(3).max(20),
    creditAcc: z.string().min(3).max(20),
    amount: z.string().regex(/^\d+(\.\d{1,2})?$/, 'Số tiền không hợp lệ.'),
    description: z.string().max(500).default(''),
    costObject: blankAsAbsent(z.string().max(60)),
    /**
     * ⭐ KHOẢN MỤC CHI PHÍ · SỐ / NGÀY HOÁ ĐƠN — ba ô của bản cũ mà bản port bỏ mất.
     * ⚠ BA Ô NÀY THEO DÒNG, không theo phiếu: một chứng từ nghiệp vụ khác có thể gom nhiều hoá đơn
     *   và nhiều khoản mục; đặt ở đầu phiếu là ép cả phiếu mang một số hoá đơn duy nhất. Cột đã có
     *   sẵn ở `accounting_doc_line` (`expense_item` · `invoice_no` · `invoice_date`) — MISA đọc
     *   theo dòng, nên bỏ trống chúng là mất hẳn thông tin lúc đẩy sang.
     */
    expenseItem: blankAsAbsent(z.string().max(60)),
    invoiceNo: blankAsAbsent(z.string().max(50)),
    invoiceDate: blankAsAbsent(ISODate),
  })).min(1).max(100),
})

export const WaitingQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
})

/**
 * ⭐ KẾT QUẢ MISA TRẢ VỀ. `ok: true` phải kèm mã chứng từ bên đó — không có mã thì không đối chiếu
 * ngược được, và "đã đẩy" trở thành một lời khẳng định không kiểm chứng được.
 */
export const PushResultBody = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true), misaRef: z.string().min(1, 'Thiếu mã chứng từ MISA trả về.') }),
  z.object({ ok: z.literal(false), error: z.string().min(1, 'Thiếu lý do MISA từ chối.') }),
])

export const DocIdParams = z.object({ id: Uuid })

export type GenerateDocBodyT = z.infer<typeof GenerateDocBody>
export type GenerateBatchBodyT = z.infer<typeof GenerateBatchBody>
/* ══ TỆP EXCEL NẠP MISA · CỜ "ĐÃ NẠP EXCEL" (`0080`) ═══════════════════════════════════════ */

/**
 * Kỳ tải. `pending=true` bỏ chứng từ đã được đánh dấu "đã nạp Excel" — đúng thao tác bản cũ khuyên:
 * lọc "chưa nạp" rồi mới tải, khỏi nạp trùng vào MISA.
 */
export const MisaSheetQuery = z.object({
  from: ISODate,
  to: ISODate,
  pending: blankAsAbsent(z.enum(['true', 'false'])),
})

/** Mẫu "Bán dịch vụ" — `ids` = chỉ các hóa đơn đang tick trên sổ chứng từ bán. */
export const MisaSalesQuery = MisaSheetQuery.extend({
  ids: blankAsAbsent(z.string().regex(/^[0-9a-f-]{36}(,[0-9a-f-]{36})*$/i, 'Danh sách chứng từ không hợp lệ.').max(8000)),
})
export type MisaSalesQueryT = z.infer<typeof MisaSalesQuery>

/** Mẫu "Mua dịch vụ": HAI tệp tách biệt — mua cước · chi ngoài (hoa hồng, chi phí đơn hàng). */
export const MisaPurchaseQuery = MisaSheetQuery.extend({
  group: z.enum(['mua', 'chi-ngoai']),
  /** Chỉ các chứng từ ĐANG TICK trên sổ chứng từ mua (mã cách nhau dấu phẩy) — bỏ trống = cả kỳ. */
  ids: blankAsAbsent(z.string().regex(/^[0-9a-f-]{36}(,[0-9a-f-]{36})*$/i, 'Danh sách chứng từ không hợp lệ.').max(8000)),
})

/**
 * Hai mẫu của CHỨNG TỪ KẾ TOÁN ("Nghiệp vụ khác" · "Mua dịch vụ" lập tay) — `ids` = chỉ các chứng từ
 * đang tick / đang mở trên màn Chứng từ (bản cũ `AmisDocs.tsx` `exportAmis`); bỏ trống = cả kỳ.
 */
export const MisaDocQuery = MisaSheetQuery.extend({
  ids: blankAsAbsent(z.string().regex(/^[0-9a-f-]{36}(,[0-9a-f-]{36})*$/i, 'Danh sách chứng từ không hợp lệ.').max(8000)),
})

/** Năm mẫu của Sổ phụ: Báo Có · Báo Nợ · Chuyển tiền nội bộ · Phiếu chi TM · Phiếu thu TM. */
export const MisaCashbookQuery = MisaSheetQuery.extend({
  kind: z.enum(['bao-co', 'bao-no', 'chuyen-tien', 'phieu-chi', 'phieu-thu']),
  /** Chỉ các phiếu ĐANG TICK trên tab Báo Nợ/Có (bản cũ `Finance.tsx:5088`) — bỏ trống = cả kỳ. */
  ids: blankAsAbsent(z.string().regex(/^[0-9a-f-]{36}(,[0-9a-f-]{36})*$/i, 'Danh sách chứng từ không hợp lệ.').max(8000)),
})

/**
 * Mẫu "Danh mục đối tượng": mốc ngày vào danh mục. `custom` dùng `from`/`to` (bỏ trống một đầu = không
 * chặn đầu đó); `nodate` = chỉ đối tượng CHƯA có ngày vào danh mục (NCC cũ).
 */
export const MisaObjectsQuery = z.object({
  range: z.enum(['today', 'week', 'lastweek', 'month', 'lastmonth', 'nodate', 'all', 'custom']),
  from: blankAsAbsent(ISODate),
  to: blankAsAbsent(ISODate),
})

export const MarkSource = z.enum(['ar_invoice', 'ap_invoice', 'bank_txn', 'accounting_doc', 'payment_doc'])

export const ExcelMarkBody = z.object({
  source: MarkSource,
  ids: z.array(Uuid).min(1).max(5000),
  on: z.boolean(),
})

export type MisaSheetQueryT = z.infer<typeof MisaSheetQuery>
export type MisaPurchaseQueryT = z.infer<typeof MisaPurchaseQuery>
export type MisaDocQueryT = z.infer<typeof MisaDocQuery>
export type MisaCashbookQueryT = z.infer<typeof MisaCashbookQuery>
export type MisaObjectsQueryT = z.infer<typeof MisaObjectsQuery>
export type ExcelMarkBodyT = z.infer<typeof ExcelMarkBody>
export type ListDocQueryT = z.infer<typeof ListDocQuery>
export type WaitingQueryT = z.infer<typeof WaitingQuery>
export type EditDocBodyT = z.infer<typeof EditDocBody>
export type CreateJournalBodyT = z.infer<typeof CreateJournalBody>
export type PushResultBodyT = z.infer<typeof PushResultBody>
